<template>
  <v-container fluid>
    <v-dialog v-model="loading" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          {{ loadingText }}
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-layout wrap justify-center>
      <custom-list-filters
        :current-filters="filters"
        :ref-name="report.id"
        @load-filters="onLoadFilters"
      ></custom-list-filters>
      <v-btn
        class="my-1 ml-4 mr-1"
        :disabled="!pdfReady"
        @click="onDownload('pdf')"
      >
        Download PDF
        <v-icon right>mdi-pdf-box</v-icon>
      </v-btn>

      <v-btn class="ma-1" :disabled="!htmlReady" @click="onDownload('html')">
        Download HTML
        <v-icon right>mdi-language-html5</v-icon>
      </v-btn>

      <v-btn class="ma-1" :disabled="!csvReady" @click="onDownload('csv')">
        Download CSV
        <v-icon right>mdi-file-delimited</v-icon>
      </v-btn>

      <v-btn class="ma-1" :disabled="!xlsxReady" @click="onDownload('xlsx')">
        Download XLSX
        <v-icon right>mdi-file-excel</v-icon>
      </v-btn>

      <report-share-menu
        :formats="reportFormats"
        :disabled="!isReady"
        @click="onShare"
      ></report-share-menu>
      <v-btn
        class="ma-1"
        color="primary"
        :loading="loading"
        @click="onGenerate"
        :disabled="generateDisabled"
      >
        Generate
        <v-icon right dark>mdi-upload</v-icon>
      </v-btn>
    </v-layout>
    <share-report-dialog
      :show="showShareDialog"
      :share-url="shareUrl"
      @close="showShareDialog = false"
      :report="report"
      :filters="filters"
      @share-with-supplier="onShareWithSupplier"
    ></share-report-dialog>
    <email-dialog
      :show="showEmailDialog"
      v-model="email"
      @close="showEmailDialog = false"
      @send="onSendMessage"
      :key="emailDialogKey"
    ></email-dialog>
    <div v-if="debug">
      <pre>{{ JSON.stringify(filters, null, 4) }}</pre>
    </div>
  </v-container>
</template>

<script>
import { GENERATE_REPORT_MUTATION } from "@/constants/graphql";
import { reportUrl } from "@/helpers";
import gql from "graphql-tag";
import ReportShareMenu from "./ReportShareMenu.vue";
import ShareReportDialog from "./ShareReportDialog.vue";
import EmailDialog from "../shared/EmailDialog.vue";
import { SEND_CLIENT_MESSAGE } from "@/constants/graphql";
import CustomListFilters from "../shared/CustomListFilters.vue";
const moment = require("moment");
const REPORT_STATUS = gql`
  query reportStatus($taskId: ID!) {
    reportStatus: taskStatus(id: $taskId) {
      status
      notes
    }
  }
`;
const SHARE_REPORT_MUTATION = gql`
  mutation shareReport($downloadFileId: ID!, $format: ShareDocFormatType!) {
    shareReport(downloadFileId: $downloadFileId, format: $format) {
      success
      error
      url
    }
  }
`;
export default {
  components: {
    ReportShareMenu,
    ShareReportDialog,
    EmailDialog,
    CustomListFilters,
  },
  name: "ReportActions",
  props: {
    report: {
      type: Object,
      default: () => ({}),
    },
    filters: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      debug: false,
      loading: false,
      taskId: null,
      reportStatus: null,
      checkStatus: false,
      dowloadFileId: "",
      shareUrl: "",
      showShareDialog: false,
      loadingText: "Generating Your Report...",
      email: null,
      showEmailDialog: false,
      emailDialogKey: Math.random(),
    };
  },

  methods: {
    onShareWithSupplier(payload) {
      this.email = {
        emailTo: payload.email,
        ccTo: this.$store.getters.userEmail,
        message: payload ? payload.message : "",
        shareLink: payload ? payload.shareLink : "",
        //multiple: true,
        //recipients: [payload.email],
        emailToLabel: "Share Report",
      };
      this.showEmailDialog = true;
      this.emailDialogKey = Math.random();
    },
    onSendMessage(payload) {
      this.$apollo
        .mutate({
          mutation: SEND_CLIENT_MESSAGE,
          variables: { input: { ...payload } },
        })
        .then((res) => {
          if (res.data.contactClient.success) {
            this.$store.dispatch("showAppSnackbar", {
              msg: "Message Sent",
            });
            this.showEmailDialog = false;
          } else {
            this.$store.dispatch("showAppSnackbar", {
              msg: res.data.contactClient.error,
              color: "error",
            });
          }
        })
        .catch((err) => {
          //console.log(err);
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
          console.log(err);
        });
    },
    onShare(item) {
      this.loadingText = "Sharing Your Report...";
      this.loading = true;
      this.$apollo
        .mutate({
          mutation: SHARE_REPORT_MUTATION,
          variables: {
            downloadFileId: this.dowloadFileId,
            format: item.name,
          },
        })
        .then((result) => {
          this.loading = false;
          if (result.data.shareReport.success) {
            this.shareUrl = result.data.shareReport.url;
            this.showShareDialog = true;
          } else {
            this.$store.dispatch("showAppSnackbar", {
              msg: result.data.shareReport.error,
              color: "error",
            });
          }
        })
        .catch((error) => {
          this.loading = false;
          const errorMsg = error.graphQLErrors.map((e) => e.message).join("\n");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    reset() {
      this.taskId = null;
      this.reportStatus = null;
      this.checkStatus = false;
      this.dowloadFileId = "";
    },
    async onGenerate() {
      this.loadingText = "Generating Your Report...";
      try {
        const { data } = await this.$apollo.mutate({
          mutation: GENERATE_REPORT_MUTATION,
          variables: {
            reportId: this.report.id,
            filters: this.filters.map((f) => ({
              name: f.name,
              value: f.value,
            })),
          },
        });
        this.taskId = data.task.id;
        this.checkStatus = true;
        this.loading = true;
      } catch (err) {
        this.$store.dispatch("showAppSnackbar", {
          msg: err,
          color: "error",
        });
        console.error("generate error", err);
      }
    },
    onDownload(fileType) {
      window.open(reportUrl(fileType)(this.dowloadFileId), "_blank");
    },
    validateDateFilters() {
      //https://tracker.globetrack.ie/issues/7798
      const dateFilters = [
        "dates",
        "dates_range",
        "service_dates",
        "itinerary_dates",
        "itinerary_creation_dates",
      ];
      const referenceFilters = ["itinerary_ref", "ref", "reference"];

      let firstDateFilter = this.report.filters.find(
        (f) =>
          dateFilters.includes(f.name) || dateFilters.includes(f.filterName)
      );
      const serviceDateFilter = this.report.filters.find((f) =>
        dateFilters.includes(f.filterName)
      );
      if (serviceDateFilter) {
        const df = this.filters.find((f) => f.name == "service_dates_from");
        const dt = this.filters.find((f) => f.name == "service_dates_to");
        if (df && dt && df.value && dt.value) {
          firstDateFilter = serviceDateFilter;
        }
      }

      //find first date range filter
      //if there is iti ref filter and its not empty - it's valid
      const ref = this.filters.find((f) => referenceFilters.includes(f.name));
      if (ref && ref.value && ref.value != "") {
        return true;
      }
      //https://tracker.globetrack.ie/issues/11289
      //if a payment date range is entered, itinerary dates are not required
      const paymentDateFilter = this.report.filters.find(
        (f) => f.filterName === "payment_date"
      );
      if (paymentDateFilter) {
        const pdf = this.filters.find((f) => f.name == "payment_date_from");
        const pdt = this.filters.find((f) => f.name == "payment_date_to");
        if (pdf && pdt && pdf.value && pdt.value) {
          const parsedPdf = moment(pdf.value, "DD-MM-YYYY", true);
          const parsedPdt = moment(pdt.value, "DD-MM-YYYY", true);
          if (
            parsedPdf.isValid() &&
            parsedPdt.isValid() &&
            parsedPdf <= parsedPdt
          ) {
            return true;
          }
        }
      }
      if (firstDateFilter) {
        //pick correct labels for filter types
        let dfLabel = "date_from";
        let dtLabel = "date_to";
        if (firstDateFilter.name == "itinerary_creation_dates") {
          dfLabel = "creation_from";
          dtLabel = "creation_to";
        }
        if (firstDateFilter.filterName == "service_dates") {
          dfLabel = "service_dates_from";
          dtLabel = "service_dates_to";
        }
        //get date "from" and "to" from filters list based ot these labels
        const df = this.filters.find((f) => f.name == dfLabel);
        const dt = this.filters.find((f) => f.name == dtLabel);
        if (df && dt && df.value && dt.value) {
          const dfy = Number(df.value.split("-")[2]);
          const dty = Number(dt.value.split("-")[2]);
          if (dfy < 1900 || dty < 1900) {
            return false;
          }
          const parsedDf = moment(df.value, "DD-MM-YYYY", true);
          const parsedDt = moment(dt.value, "DD-MM-YYYY", true);
          //console.log(df.value,dt.value,parsedDf.isValid(),parsedDt.isValid(),parsedDf <= parsedDt);
          return (
            parsedDf.isValid() && parsedDt.isValid() && parsedDf <= parsedDt
          );
        }
        return false; //if filter exists and both values are not selected yet - it's not valid
      }
      return true; //if no date range filter - it's valid
    },
    validateReportSpecificFilters() {
      const requireDestinationOrSupplierFor = [
        "Supplier Destination",
        "Supplier Destination Grouped",
      ]; // https://tracker.globetrack.ie/issues/10949
      if (requireDestinationOrSupplierFor.includes(this.report.name)) {
        const destinationFilter = this.filters.find(
          (f) => f.name === "destination" || f.name === "destination_name"
        );
        const supplierFilter = this.filters.find(
          (f) => f.name === "supplier_id"
        );
        const hasDestination =
          destinationFilter && destinationFilter.value && destinationFilter.value !== "";
        const hasSupplier =
          supplierFilter && supplierFilter.value && supplierFilter.value !== "";
        if (!hasDestination && !hasSupplier) {
          return false;
        }
      }
      return true;
    },
    onLoadFilters(payload) {
      //console.log("onLoadFilters", payload);
      this.$emit("load-filters", payload);
    },
  },

  computed: {
    generateDisabled() {
      //console.log("this.report",this.report);
      //console.log("this.filters",this.filters);
      return (
        !this.report.id ||
        this.filters.length == 0 ||
        !this.validateDateFilters() ||
        !this.validateReportSpecificFilters()
      );
    },
    isReady() {
      return this.dowloadFileId != "";
    },
    htmlReady() {
      return (
        this.dowloadFileId != "" && this.report.formats.indexOf("HTML") != -1
      );
    },
    csvReady() {
      return (
        this.dowloadFileId != "" && this.report.formats.indexOf("CSV") != -1
      );
    },
    xlsxReady() {
      return (
        this.dowloadFileId != "" && this.report.formats.indexOf("XLSX") != -1
      );
    },
    pdfReady() {
      return (
        this.dowloadFileId != "" && this.report.formats.indexOf("PDF") != -1
      );
    },
    reportFormats() {
      return this.dowloadFileId != "" ? [...this.report.formats] : [];
    },
  },

  watch: {
    report(data) {
      //console.log("set report", data);
      this.reset();
    },
  },

  apollo: {
    reportStatus: {
      query: REPORT_STATUS,
      variables() {
        return { taskId: this.taskId };
      },
      result({ data, loader, networkStatus }) {
        this.checkStatus = false;
        const status = data.reportStatus.status;
        if (status == "IN_PROGRESS") {
          setTimeout(() => {
            this.checkStatus = true;
          }, 2000);
        } else {
          if (status == "ERROR") {
            //show error message
            console.error(data.reportStatus.notes);
            let errorMsg = data.reportStatus.notes;
            if (errorMsg.includes("|||")) {
              errorMsg = errorMsg.split("|||")[0];
            }
            this.$store.dispatch("showAppSnackbar", {
              msg: errorMsg,
              color: "error",
            });
          } else {
            this.dowloadFileId = data.reportStatus.notes;
          }
          this.loading = false;
          this.checkStatus = false;
        }
      },
      skip() {
        return !this.checkStatus;
      },
      fetchPolicy: "network-only",
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/reports/ReportActions.vue